// ObstacleDragControls.js
import * as THREE from 'three';
import { DragControls } from 'three/addons/controls/DragControls.js';
import { POLYGONS } from '../Global/variables';
import { sceneManager } from "../SetUps/SceneManager";
import { createSegmentPlane } from "../Global/functions";
import { updateObstacleShape } from "./Modifications";
import { updateLinkedSegmentsAfterMove } from "./ObstacleManager";
import { pushUndoAction } from "../Global/UndoManager";

let obstacleDragControls = null;
let rotationDragControls = null;
let edgeDragControls = null;

// null | 'vertex' | 'rotate' | 'translate'
let currentDragMode = null;

let obstacleHandles = [];
let rotationHandles = [];
let edgeHandles = [];
let active = null;

// For detecting clicks outside the handles
const raycaster = new THREE.Raycaster();
let pointerDownListener = null;

// For hover cursor changes
let hoverMoveListener = null;
let currentCursor = 'auto';

// Rotation drag state
let rotationState = null;

// Translation drag state
let translateState = null;

// Vertex drag state
let vertexDragState = null;

// ---------------------------------------------------------------------
// Segment polygon utilities (used to validate obstacle stays attached)
// ---------------------------------------------------------------------

function getSegmentPolygon2D(segmentIndex) {
    const segment = POLYGONS[segmentIndex];
    if (!segment || !segment.edges || !segment.edges.length) return null;

    const segPoints2D = [];
    segment.edges.forEach(edge => {
        if (edge.point1) segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
        if (edge.point2) segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });

    const uniquePoints = [];
    for (let p of segPoints2D) {
        if (!uniquePoints.some(u => u.distanceTo(p) < 1e-6)) uniquePoints.push(p);
    }
    if (!uniquePoints.length) return null;

    const center = uniquePoints
        .reduce((acc, p) => acc.add(p), new THREE.Vector2())
        .divideScalar(uniquePoints.length);

    uniquePoints.sort(
        (a, b) =>
            Math.atan2(a.y - center.y, a.x - center.x) -
            Math.atan2(b.y - center.y, b.x - center.x)
    );

    return uniquePoints;
}

function pointInSegmentPolygon2D(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;

        const intersect =
            ((yi > point.y) !== (yj > point.y)) &&
            (point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi);

        if (intersect) inside = !inside;
    }
    return inside;
}

function isWorldPointInsideSegment(point, segmentIndex) {
    const poly = getSegmentPolygon2D(segmentIndex);
    if (!poly) return false;

    const p2d = new THREE.Vector2(point.x, point.y);
    return pointInSegmentPolygon2D(p2d, poly);
}

// ---------------------------------------------------------------------
// Utility: Enable/Disable raycast on the obstacle mesh itself
// ---------------------------------------------------------------------
function setObstacleRaycastEnabled(obstruction, enabled) {
    if (!obstruction?.mesh) return;

    if (enabled) {
        if (obstruction._originalRaycast) {
            obstruction.mesh.raycast = obstruction._originalRaycast;
            obstruction._originalRaycast = null;
        }
    } else {
        if (!obstruction._originalRaycast) {
            obstruction._originalRaycast = obstruction.mesh.raycast;
            obstruction.mesh.raycast = function () { /* no-op */ };
        }
    }
}

// ---------------------------------------------------------------------
// Utility: Changing the DOM cursor
// ---------------------------------------------------------------------
function setDomCursor(domElement, cursor) {
    if (!domElement || currentCursor === cursor) return;
    domElement.style.cursor = cursor;
    currentCursor = cursor;
}

// ---------------------------------------------------------------------
// Utility: Create corner + rotation + edge handles
// ---------------------------------------------------------------------
function createCornerHandle(position, index) {
    const sphereGeo = new THREE.SphereGeometry(0.1, 16, 16);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    const handle = new THREE.Mesh(sphereGeo, sphereMat);
    handle.position.copy(position);
    handle.userData.vertexIndex = index;
    handle.userData.isCornerHandle = true;
    return handle;
}

function createRotationHandle(position, center, index) {
    const sphereGeo = new THREE.SphereGeometry(0.07, 16, 16);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 0x00ffff });
    const handle = new THREE.Mesh(sphereGeo, sphereMat);
    handle.position.copy(position);
    handle.userData.vertexIndex = index;
    handle.userData.isRotationHandle = true;
    return handle;
}

function createEdgeHandle(position, index) {
    const sphereGeo = new THREE.SphereGeometry(0.09, 16, 16);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const handle = new THREE.Mesh(sphereGeo, sphereMat);
    handle.position.copy(position);
    handle.userData.edgeIndex = index;
    handle.userData.isEdgeHandle = true;
    return handle;
}

// ---------------------------------------------------------------------
// Utility: Signed angle between two vectors around a normal
// ---------------------------------------------------------------------
function signedAngleBetween(v1, v2, normal) {
    const v1n = v1.clone().normalize();
    const v2n = v2.clone().normalize();
    const dot = THREE.MathUtils.clamp(v1n.dot(v2n), -1, 1);
    const angle = Math.acos(dot);
    const cross = new THREE.Vector3().crossVectors(v1n, v2n);
    const sign = Math.sign(cross.dot(normal));
    return angle * sign;
}

// ---------------------------------------------------------------------
// Exit edit mode when click outside handles
// ---------------------------------------------------------------------
function attachPointerDownExitListener() {
    const domElement = sceneManager.renderer.domElement;
    const camera = sceneManager.camera;

    if (pointerDownListener) return;

    pointerDownListener = (event) => {
        if (!active) return;
        if (
            (obstacleHandles.length === 0) &&
            (rotationHandles.length === 0) &&
            (edgeHandles.length === 0)
        ) return;

        const rect = domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -(((event.clientY - rect.top) / rect.height) * 2 - 1)
        );

        raycaster.setFromCamera(ndc, camera);

        const allHandles = [...obstacleHandles, ...rotationHandles, ...edgeHandles];
        const intersects = raycaster.intersectObjects(allHandles, true);

        if (intersects.length > 0) return;

        disableObstacleVertexDrag();
    };

    domElement.addEventListener('pointerdown', pointerDownListener);
}

function detachPointerDownExitListener() {
    const domElement = sceneManager.renderer.domElement;
    if (pointerDownListener) {
        domElement.removeEventListener('pointerdown', pointerDownListener);
        pointerDownListener = null;
    }
}

// ---------------------------------------------------------------------
// Hover cursor listener
// ---------------------------------------------------------------------
function attachHoverCursorListener(segmentIndex, obstacleIndex) {
    const domElement = sceneManager.renderer.domElement;
    const camera = sceneManager.camera;

    if (hoverMoveListener) return;

    hoverMoveListener = (event) => {
        if (!active) return;

        const obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];
        if (!obstruction) return;

        const rect = domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -(((event.clientY - rect.top) / rect.height) * 2 - 1)
        );

        raycaster.setFromCamera(ndc, camera);

        let intersects = raycaster.intersectObjects(obstacleHandles, true);
        if (intersects.length > 0) {
            setDomCursor(domElement, 'nwse-resize');
            return;
        }

        intersects = raycaster.intersectObjects(rotationHandles, true);
        if (intersects.length > 0) {
            setDomCursor(domElement, 'alias');
            return;
        }

        intersects = raycaster.intersectObjects(edgeHandles, true);
        if (intersects.length > 0) {
            setDomCursor(domElement, 'grab');
            return;
        }

        if (obstruction && obstruction.mesh) {
            intersects = raycaster.intersectObject(obstruction.mesh, true);
            if (intersects.length > 0) {
                setDomCursor(domElement, 'grab');
                return;
            }
        }

        setDomCursor(domElement, 'auto');
    };

    domElement.addEventListener('mousemove', hoverMoveListener);
}

function detachHoverCursorListener() {
    const domElement = sceneManager.renderer.domElement;
    if (hoverMoveListener) {
        domElement.removeEventListener('mousemove', hoverMoveListener);
        hoverMoveListener = null;
    }
}

export function isObstacleEditDragging() {
    return currentDragMode !== null;
}

// ---------------------------------------------------------------------
// Helper: mark obstacle dirty (unflushed) after edits
// ---------------------------------------------------------------------
function markObstacleDirty(obstruction) {
    if (!obstruction) return;
    obstruction.isFlushed = false;

    // Keep it strictly on parent until next flush
    if (typeof obstruction.parentSegment === "number") {
        obstruction.linkedSegments = [obstruction.parentSegment];
    }
    updateLinkedSegmentsAfterMove(obstruction);
}

export function enableObstacleVertexDrag(segmentIndex, obstacleIndex) {
    disableObstacleVertexDrag();

    const obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];
    if (!obstruction || !obstruction.cords || obstruction.cords.length < 4) return;

    const parentSegmentIndex =
        typeof obstruction.parentSegment === 'number'
            ? obstruction.parentSegment
            : segmentIndex;

    active = { segmentIndex, obstacleIndex, parentSegmentIndex };

    const plane = createSegmentPlane(parentSegmentIndex);

    setObstacleRaycastEnabled(obstruction, false);

    const basePoints = obstruction.cords.slice(0, obstruction.cords.length - 1);

    const center = new THREE.Vector3();
    basePoints.forEach(p => center.add(p));
    center.multiplyScalar(1 / basePoints.length);

    const scene = sceneManager.scene;
    const domElement = sceneManager.renderer.domElement;
    const camera = sceneManager.camera;

    obstacleHandles.forEach(h => {
        h.removeFromParent();
        if (h.geometry) h.geometry.dispose();
        if (h.material) h.material.dispose();
    });
    obstacleHandles = [];

    rotationHandles.forEach(h => {
        h.removeFromParent();
        if (h.geometry) h.geometry.dispose();
        if (h.material) h.material.dispose();
    });
    rotationHandles = [];

    edgeHandles.forEach(h => {
        h.removeFromParent();
        if (h.geometry) h.geometry.dispose();
        if (h.material) h.material.dispose();
    });
    edgeHandles = [];

    const handleOffset = 0.5;

    const isCircle = obstruction.type === "circle";

    if (isCircle) {
        let radiusDir = basePoints[0].clone().sub(center);
        if (radiusDir.lengthSq() < 1e-6 && basePoints.length > 1) {
            radiusDir = basePoints[Math.floor(basePoints.length / 4)]
                .clone()
                .sub(center);
        }
        if (radiusDir.lengthSq() < 1e-6) return;

        const radius = Math.sqrt(radiusDir.lengthSq());
        const dirNorm = radiusDir.clone().normalize();

        const scaleHandlePos = center.clone().add(dirNorm.clone().multiplyScalar(radius));
        const translateHandlePos = center.clone().sub(dirNorm.clone().multiplyScalar(radius));

        const scaleHandle = createCornerHandle(scaleHandlePos, 0);
        scaleHandle.userData.isCircleScale = true;
        scene.add(scaleHandle);
        obstacleHandles.push(scaleHandle);

        rotationHandles = [];

        const translateHandle = createEdgeHandle(translateHandlePos, 0);
        translateHandle.userData.centerOffset = translateHandlePos.clone().sub(center);
        translateHandle.userData.isCircleTranslate = true;
        scene.add(translateHandle);
        edgeHandles.push(translateHandle);
    } else {
        for (let i = 0; i < basePoints.length; i++) {
            const cornerPos = basePoints[i].clone();
            const cornerHandle = createCornerHandle(cornerPos, i);
            scene.add(cornerHandle);
            obstacleHandles.push(cornerHandle);

            const dirFromCenter = cornerPos.clone().sub(center);
            let rotationPos;
            if (dirFromCenter.lengthSq() > 0.0001) {
                const dirNorm = dirFromCenter.clone().normalize();
                rotationPos = cornerPos.clone().add(dirNorm.multiplyScalar(handleOffset));
            } else {
                rotationPos = cornerPos.clone().add(new THREE.Vector3(0, handleOffset, 0));
            }

            const rotationHandle = createRotationHandle(rotationPos, center, i);
            rotationHandle.userData.baseDir = rotationPos.clone().sub(center);
            scene.add(rotationHandle);
            rotationHandles.push(rotationHandle);
        }

        for (let i = 0; i < basePoints.length; i++) {
            const p1 = basePoints[i];
            const p2 = basePoints[(i + 1) % basePoints.length];

            const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);
            const edgeHandle = createEdgeHandle(mid, i);

            edgeHandle.userData.centerOffset = mid.clone().sub(center);

            scene.add(edgeHandle);
            edgeHandles.push(edgeHandle);
        }
    }

    setDomCursor(domElement, 'nwse-resize');

    // ---------------------------------------------
    // Corner DragControls (resize)
    // ---------------------------------------------
    obstacleDragControls = new DragControls(obstacleHandles, camera, domElement);

    obstacleDragControls.addEventListener('dragstart', (event) => {
        if (currentDragMode && currentDragMode !== 'vertex') return;
        currentDragMode = 'vertex';

        if (rotationDragControls) rotationDragControls.deactivate();
        if (edgeDragControls) edgeDragControls.deactivate();

        if (sceneManager.controls) sceneManager.controls.enabled = false;
        setDomCursor(domElement, 'grabbing');

        vertexDragState = null;
        if (active) {
            const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
            const obstructionLocal = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            const handle = event.object;

            if (!obstructionLocal || !handle) return;

            const originalLinkedSegments = Array.isArray(obstructionLocal.linkedSegments)
                ? obstructionLocal.linkedSegments.slice()
                : [];

            if (obstructionLocal.type === "circle") {
                const baseOriginal = obstructionLocal.cords
                    .slice(0, obstructionLocal.cords.length - 1)
                    .map(p => p.clone());

                const centerOriginal = new THREE.Vector3();
                baseOriginal.forEach(p => centerOriginal.add(p));
                centerOriginal.multiplyScalar(1 / baseOriginal.length);

                vertexDragState = {
                    segmentIndex: segIdx,
                    obstacleIndex: obsIdx,
                    center: centerOriginal,
                    originalPoints: baseOriginal,
                    originalCords: obstructionLocal.cords.map(p => p.clone()),
                    originalIsFlushed: obstructionLocal.isFlushed === true,
                    originalLinkedSegments,
                    moved: false,
                    isCircle: true,
                };
            } else if (typeof handle.userData.vertexIndex === 'number') {
                const vIndex = handle.userData.vertexIndex;
                vertexDragState = {
                    segmentIndex: segIdx,
                    obstacleIndex: obsIdx,
                    vertexIndex: vIndex,
                    lastValidPosition: obstructionLocal.cords[vIndex].clone(),
                    originalCords: obstructionLocal.cords.map(p => p.clone()),
                    originalIsFlushed: obstructionLocal.isFlushed === true,
                    originalLinkedSegments,
                    moved: false,
                    isCircle: false,
                };
            }
        }
    });

    obstacleDragControls.addEventListener('drag', (event) => {
        if (currentDragMode !== 'vertex') return;
        if (!active) return;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction) return;

        const handle = event.object;

        // CIRCLE scale (plane only)
        if (obstruction.type === "circle" && vertexDragState && vertexDragState.isCircle) {
            const { center: centerLocal, originalPoints } = vertexDragState;

            const newPos = new THREE.Vector3();
            plane.projectPoint(handle.position, newPos);

            const dir = newPos.clone().sub(centerLocal);
            if (dir.lengthSq() < 1e-10) return;
            const newRadius = Math.sqrt(dir.lengthSq());
            const radialDir = dir.clone().normalize();

            const finalBasePoints = [];
            for (let i = 0; i < originalPoints.length; i++) {
                const op = originalPoints[i];
                let d = op.clone().sub(centerLocal);
                if (d.lengthSq() < 1e-10) {
                    d = radialDir.clone().multiplyScalar(newRadius);
                } else {
                    d.normalize().multiplyScalar(newRadius);
                }
                const candidate = centerLocal.clone().add(d);

                obstruction.cords[i].copy(candidate);
                finalBasePoints.push(candidate);
            }

            obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

            updateObstacleShape(segIdx, obsIdx, obstruction.cords);

            const newCenter = new THREE.Vector3();
            finalBasePoints.forEach(p => newCenter.add(p));
            newCenter.multiplyScalar(1 / finalBasePoints.length);

            const scaleDir = radialDir.clone().normalize();
            const scaleHandlePos = newCenter.clone().add(scaleDir.clone().multiplyScalar(newRadius));
            const translateHandlePos = newCenter.clone().sub(scaleDir.clone().multiplyScalar(newRadius));

            if (obstacleHandles[0]) {
                obstacleHandles[0].position.copy(scaleHandlePos);
            }
            if (edgeHandles[0]) {
                edgeHandles[0].position.copy(translateHandlePos);
                edgeHandles[0].userData.centerOffset = translateHandlePos.clone().sub(newCenter);
            }

            vertexDragState.moved = true;
            return;
        }

        // RECT/POLYGON vertex move (plane only — NO MODEL RAYCAST)
        const i = handle.userData.vertexIndex;
        const newPos = new THREE.Vector3();
        plane.projectPoint(handle.position, newPos);

        handle.position.copy(newPos);
        obstruction.cords[i].copy(newPos);

        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

        updateObstacleShape(segIdx, obsIdx, obstruction.cords);

        const pts = obstruction.cords;
        const basePointsUpdated = pts.slice(0, pts.length - 1);

        const newCenter = new THREE.Vector3();
        basePointsUpdated.forEach(p => newCenter.add(p));
        newCenter.multiplyScalar(1 / basePointsUpdated.length);

        for (let j = 0; j < obstacleHandles.length; j++) {
            obstacleHandles[j].position.copy(basePointsUpdated[j]);
        }

        for (let j = 0; j < rotationHandles.length; j++) {
            const corner = basePointsUpdated[j].clone();
            const dirFromCenter = corner.clone().sub(newCenter);
            let handlePos;
            if (dirFromCenter.lengthSq() > 0.0001) {
                const dirNorm = dirFromCenter.clone().normalize();
                handlePos = corner.clone().add(dirNorm.multiplyScalar(handleOffset));
            } else {
                handlePos = corner.clone().add(new THREE.Vector3(0, handleOffset, 0));
            }
            rotationHandles[j].position.copy(handlePos);
            rotationHandles[j].userData.baseDir = handlePos.clone().sub(newCenter);
        }

        for (let j = 0; j < edgeHandles.length; j++) {
            const p1 = basePointsUpdated[j];
            const p2 = basePointsUpdated[(j + 1) % basePointsUpdated.length];
            const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

            edgeHandles[j].position.copy(mid);
            edgeHandles[j].userData.centerOffset = mid.clone().sub(newCenter);
        }

        setObstacleRaycastEnabled(obstruction, false);

        if (vertexDragState) vertexDragState.moved = true;
    });

    obstacleDragControls.addEventListener('dragend', () => {
        if (currentDragMode !== 'vertex') return;

        if (active && vertexDragState) {
            const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
            const obstruction = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            const isCircleLocal = obstruction?.type === "circle";

            if (!isCircleLocal) {
                const { parentSegmentIndex } = active;
                const { vertexIndex, lastValidPosition } = vertexDragState;

                if (obstruction && obstruction.cords && obstruction.cords.length > 1) {
                    const ownerSegment =
                        typeof obstruction.parentSegment === 'number'
                            ? obstruction.parentSegment
                            : (typeof parentSegmentIndex === 'number'
                                ? parentSegmentIndex
                                : segIdx);

                    const baseVertices = obstruction.cords.slice(0, obstruction.cords.length - 1);
                    let insideCount = 0;
                    for (let v of baseVertices) {
                        if (isWorldPointInsideSegment(v, ownerSegment)) insideCount++;
                    }

                    if (insideCount === 0 && lastValidPosition) {
                        obstruction.cords[vertexIndex].copy(lastValidPosition);
                        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);
                        updateObstacleShape(segIdx, obsIdx, obstruction.cords);

                        const pts = obstruction.cords;
                        const basePointsUpdated = pts.slice(0, pts.length - 1);

                        const newCenter = new THREE.Vector3();
                        basePointsUpdated.forEach(p => newCenter.add(p));
                        newCenter.multiplyScalar(1 / basePointsUpdated.length);

                        for (let j = 0; j < obstacleHandles.length; j++) {
                            obstacleHandles[j].position.copy(basePointsUpdated[j]);
                        }

                        const handleOffsetLocal = 0.5;
                        for (let j = 0; j < rotationHandles.length; j++) {
                            const corner = basePointsUpdated[j].clone();
                            const dirFromCenter = corner.clone().sub(newCenter);
                            let handlePos;
                            if (dirFromCenter.lengthSq() > 0.0001) {
                                const dirNorm = dirFromCenter.clone().normalize();
                                handlePos = corner.clone().add(dirNorm.multiplyScalar(handleOffsetLocal));
                            } else {
                                handlePos = corner.clone().add(new THREE.Vector3(0, handleOffsetLocal, 0));
                            }
                            rotationHandles[j].position.copy(handlePos);
                            rotationHandles[j].userData.baseDir = handlePos.clone().sub(newCenter);
                        }

                        for (let j = 0; j < edgeHandles.length; j++) {
                            const p1 = basePointsUpdated[j];
                            const p2 = basePointsUpdated[(j + 1) % basePointsUpdated.length];
                            const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

                            edgeHandles[j].position.copy(mid);
                            edgeHandles[j].userData.centerOffset = mid.clone().sub(newCenter);
                        }

                        vertexDragState.moved = false;
                    }
                }
            }
        }

        // Undo/redo + dirty marking
        if (vertexDragState && vertexDragState.moved) {
            const {
                segmentIndex: segIdx,
                obstacleIndex: obsIdx,
                originalCords,
                originalIsFlushed,
                originalLinkedSegments,
            } = vertexDragState;

            const obstructionUndo = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            if (!obstructionUndo) {
                vertexDragState = null;
            } else {
                const finalCords = obstructionUndo.cords.map(p => p.clone());

                // ✅ mark dirty after edit
                markObstacleDirty(obstructionUndo);

                pushUndoAction({
                    label: obstructionUndo.type === "circle" ? "Resize circle obstacle" : "Edit obstacle vertices",
                    undo: () => {
                        const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                        if (!o) return;
                        updateObstacleShape(segIdx, obsIdx, originalCords);
                        o.isFlushed = originalIsFlushed;
                        o.linkedSegments = Array.isArray(originalLinkedSegments) ? originalLinkedSegments.slice() : [];
                        updateLinkedSegmentsAfterMove(o);
                    },
                    redo: () => {
                        const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                        if (!o) return;
                        updateObstacleShape(segIdx, obsIdx, finalCords);
                        markObstacleDirty(o);
                    },
                });
            }
        }

        vertexDragState = null;
        currentDragMode = null;

        if (sceneManager.controls) sceneManager.controls.enabled = true;
        setDomCursor(domElement, 'nwse-resize');

        if (rotationDragControls) rotationDragControls.activate();
        if (edgeDragControls) edgeDragControls.activate();
    });

    // ---------------------------------------------
    // Rotation DragControls
    // ---------------------------------------------
    rotationDragControls = new DragControls(rotationHandles, camera, domElement);

    rotationDragControls.addEventListener('dragstart', (event) => {
        if (currentDragMode && currentDragMode !== 'rotate') return;
        if (!active) return;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction || obstruction.type === "circle") return;

        currentDragMode = 'rotate';

        if (obstacleDragControls) obstacleDragControls.deactivate();
        if (edgeDragControls) edgeDragControls.deactivate();

        if (sceneManager.controls) sceneManager.controls.enabled = false;

        const { parentSegmentIndex } = active;

        const basePointsOriginal = obstruction.cords
            .slice(0, obstruction.cords.length - 1)
            .map(p => p.clone());

        const centerOriginal = new THREE.Vector3();
        basePointsOriginal.forEach(p => centerOriginal.add(p));
        centerOriginal.multiplyScalar(1 / basePointsOriginal.length);

        const ownerSegment =
            typeof obstruction.parentSegment === 'number'
                ? obstruction.parentSegment
                : (typeof parentSegmentIndex === 'number'
                    ? parentSegmentIndex
                    : segIdx);

        const planeOriginal = createSegmentPlane(ownerSegment);

        const handle = event.object;
        const baseDir = handle.userData.baseDir.clone();

        rotationState = {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane: planeOriginal,
            center: centerOriginal,
            originalPoints: basePointsOriginal,
            baseDir: baseDir,
            originalCords: obstruction.cords.map(p => p.clone()),
            originalIsFlushed: obstruction.isFlushed === true,
            originalLinkedSegments: Array.isArray(obstruction.linkedSegments) ? obstruction.linkedSegments.slice() : [],
            moved: false,
            currentAngle: 0,
        };

        setDomCursor(sceneManager.renderer.domElement, 'alias');
    });

    rotationDragControls.addEventListener('drag', (event) => {
        if (currentDragMode !== 'rotate') return;
        if (!rotationState) return;

        const {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane,
            center,
            originalPoints,
            baseDir,
        } = rotationState;

        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction || obstruction.type === "circle") return;

        const handle = event.object;

        const projected = new THREE.Vector3();
        plane.projectPoint(handle.position, projected);
        handle.position.copy(projected);

        const currentDir = projected.clone().sub(center);
        if (currentDir.lengthSq() < 1e-8) return;

        const normal = plane.normal.clone().normalize();
        const angle = signedAngleBetween(baseDir, currentDir, normal);

        rotationState.currentAngle = angle;

        for (let i = 0; i < originalPoints.length; i++) {
            const v = originalPoints[i].clone().sub(center);
            v.applyAxisAngle(normal, angle);
            const rotatedPos = center.clone().add(v);
            obstruction.cords[i].copy(rotatedPos);
        }

        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

        updateObstacleShape(segIdx, obsIdx, obstruction.cords);

        const pts = obstruction.cords;
        const basePointsUpdated = pts.slice(0, pts.length - 1);

        const newCenter = new THREE.Vector3();
        basePointsUpdated.forEach(p => newCenter.add(p));
        newCenter.multiplyScalar(1 / basePointsUpdated.length);

        for (let i = 0; i < obstacleHandles.length; i++) {
            obstacleHandles[i].position.copy(basePointsUpdated[i]);
        }

        const handleOffsetLocal = 0.5;
        for (let i = 0; i < rotationHandles.length; i++) {
            const corner = basePointsUpdated[i].clone();
            const dirFromCenter = corner.clone().sub(newCenter);
            let handlePos;
            if (dirFromCenter.lengthSq() > 0.0001) {
                const dirNorm = dirFromCenter.clone().normalize();
                handlePos = corner.clone().add(dirNorm.multiplyScalar(handleOffsetLocal));
            } else {
                handlePos = corner.clone().add(new THREE.Vector3(0, handleOffsetLocal, 0));
            }
            rotationHandles[i].position.copy(handlePos);
            rotationHandles[i].userData.baseDir = handlePos.clone().sub(newCenter);
        }

        for (let i = 0; i < edgeHandles.length; i++) {
            const p1 = basePointsUpdated[i];
            const p2 = basePointsUpdated[(i + 1) % basePointsUpdated.length];
            const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

            edgeHandles[i].position.copy(mid);
            edgeHandles[i].userData.centerOffset = mid.clone().sub(newCenter);
        }

        if (Math.abs(angle) > 1e-4 && rotationState) rotationState.moved = true;

        setDomCursor(domElement, 'alias');
    });

    rotationDragControls.addEventListener('dragend', () => {
        if (currentDragMode !== 'rotate') return;

        if (rotationState && rotationState.moved) {
            const {
                segmentIndex: segIdx,
                obstacleIndex: obsIdx,
                plane,
                center,
                originalPoints,
                originalCords,
                originalIsFlushed,
                originalLinkedSegments,
                currentAngle,
            } = rotationState;

            const obstruction = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            if (obstruction && obstruction.type !== "circle") {
                const normal = plane.normal.clone().normalize();

                // ✅ Final rotation (NO SNAP) — snap only when user clicks ✅ tick
                const finalBase = originalPoints.map(p => {
                    const v = p.clone().sub(center);
                    v.applyAxisAngle(normal, currentAngle);
                    return center.clone().add(v);
                });

                const finalCords = finalBase.map(p => p.clone());
                finalCords.push(finalCords[0].clone());

                updateObstacleShape(segIdx, obsIdx, finalCords);

                // mark dirty
                markObstacleDirty(obstruction);

                const finalCordsSnapshot = obstruction.cords.map(p => p.clone());

                pushUndoAction({
                    label: "Rotate obstacle",
                    undo: () => {
                        const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                        if (!o) return;
                        updateObstacleShape(segIdx, obsIdx, originalCords);
                        o.isFlushed = originalIsFlushed;
                        o.linkedSegments = Array.isArray(originalLinkedSegments) ? originalLinkedSegments.slice() : [];
                        updateLinkedSegmentsAfterMove(o);
                    },
                    redo: () => {
                        const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                        if (!o) return;
                        updateObstacleShape(segIdx, obsIdx, finalCordsSnapshot);
                        markObstacleDirty(o);
                    },
                });
            }
        }

        currentDragMode = null;
        rotationState = null;

        if (sceneManager.controls) sceneManager.controls.enabled = true;
        setDomCursor(sceneManager.renderer.domElement, 'alias');

        if (obstacleDragControls) obstacleDragControls.activate();
        if (edgeDragControls) edgeDragControls.activate();
    });

    // ---------------------------------------------
    // Edge DragControls (translate)
    // ---------------------------------------------
    edgeDragControls = new DragControls(edgeHandles, camera, domElement);

    edgeDragControls.addEventListener('dragstart', (event) => {
        if (currentDragMode && currentDragMode !== 'translate') return;
        if (!active) return;

        currentDragMode = 'translate';

        if (obstacleDragControls) obstacleDragControls.deactivate();
        if (rotationDragControls) rotationDragControls.deactivate();

        if (sceneManager.controls) sceneManager.controls.enabled = false;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx, parentSegmentIndex } = active;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction) return;

        const basePointsOriginal = obstruction.cords
            .slice(0, obstruction.cords.length - 1)
            .map(p => p.clone());

        const centerOriginal = new THREE.Vector3();
        basePointsOriginal.forEach(p => centerOriginal.add(p));
        centerOriginal.multiplyScalar(1 / basePointsOriginal.length);

        const ownerSegment =
            typeof obstruction.parentSegment === 'number'
                ? obstruction.parentSegment
                : (typeof parentSegmentIndex === 'number'
                    ? parentSegmentIndex
                    : segIdx);

        const planeOriginal = createSegmentPlane(ownerSegment);

        const originalCords = obstruction.cords.map(p => p.clone());
        const originalLinkedSegments = Array.isArray(obstruction.linkedSegments) ? obstruction.linkedSegments.slice() : [];

        translateState = {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane: planeOriginal,
            center: centerOriginal,
            originalPoints: basePointsOriginal,
            originalCords,
            originalIsFlushed: obstruction.isFlushed === true,
            originalLinkedSegments,
            ownerSegmentIndex: ownerSegment,
            moved: false,
        };

        setDomCursor(domElement, 'grabbing');
    });

    edgeDragControls.addEventListener('drag', (event) => {
        if (currentDragMode !== 'translate') return;
        if (!translateState) return;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx, plane, center, originalPoints } = translateState;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction) return;

        const handle = event.object;

        const projected = new THREE.Vector3();
        plane.projectPoint(handle.position, projected);
        handle.position.copy(projected);

        const centerOffset = handle.userData.centerOffset.clone();
        const newCenter = projected.clone().sub(centerOffset);
        const delta = newCenter.clone().sub(center);

        for (let i = 0; i < originalPoints.length; i++) {
            const moved = originalPoints[i].clone().add(delta);
            obstruction.cords[i].copy(moved);
        }

        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

        updateObstacleShape(segIdx, obsIdx, obstruction.cords);

        const pts = obstruction.cords;
        const basePointsUpdated = pts.slice(0, pts.length - 1);

        const updatedCenter = new THREE.Vector3();
        basePointsUpdated.forEach(p => updatedCenter.add(p));
        updatedCenter.multiplyScalar(1 / basePointsUpdated.length);

        for (let i = 0; i < obstacleHandles.length; i++) {
            obstacleHandles[i].position.copy(basePointsUpdated[Math.min(i, basePointsUpdated.length - 1)]);
        }

        const handleOffsetLocal = 0.5;
        for (let i = 0; i < rotationHandles.length; i++) {
            const corner = basePointsUpdated[i].clone();
            const dirFromCenter = corner.clone().sub(updatedCenter);
            let handlePos;
            if (dirFromCenter.lengthSq() > 0.0001) {
                const dirNorm = dirFromCenter.clone().normalize();
                handlePos = corner.clone().add(dirNorm.multiplyScalar(handleOffsetLocal));
            } else {
                handlePos = corner.clone().add(new THREE.Vector3(0, handleOffsetLocal, 0));
            }
            rotationHandles[i].position.copy(handlePos);
            rotationHandles[i].userData.baseDir = handlePos.clone().sub(updatedCenter);
        }

        for (let i = 0; i < edgeHandles.length; i++) {
            const p1 = basePointsUpdated[i % basePointsUpdated.length];
            const p2 = basePointsUpdated[(i + 1) % basePointsUpdated.length];
            const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

            edgeHandles[i].position.copy(mid);
            edgeHandles[i].userData.centerOffset = mid.clone().sub(updatedCenter);
        }

        if (delta.lengthSq() > 1e-8 && translateState) translateState.moved = true;

        setDomCursor(domElement, 'grabbing');
    });

    edgeDragControls.addEventListener('dragend', (event) => {
        if (currentDragMode !== 'translate') return;
        if (!translateState) return;

        const {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane,
            center,
            originalPoints,
            originalCords,
            originalIsFlushed,
            originalLinkedSegments,
            ownerSegmentIndex,
        } = translateState;

        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];

        if (obstruction) {
            const handle = event.object;

            const projected = new THREE.Vector3();
            plane.projectPoint(handle.position, projected);

            const centerOffset = handle.userData.centerOffset.clone();
            const newCenter = projected.clone().sub(centerOffset);
            const diff = newCenter.clone().sub(center);

            // ✅ Final translate (NO SNAP) — snap only when user clicks ✅ tick
            const finalBase = originalPoints.map(p => p.clone().add(diff));

            const finalCords = finalBase.map(p => p.clone());
            finalCords.push(finalCords[0].clone());

            const baseVertices = finalCords.slice(0, finalCords.length - 1);
            let insideCount = 0;
            baseVertices.forEach(v => {
                if (isWorldPointInsideSegment(v, ownerSegmentIndex)) insideCount++;
            });

            if (insideCount === 0) {
                updateObstacleShape(segIdx, obsIdx, originalCords);
                updateLinkedSegmentsAfterMove(obstruction);
                translateState.moved = false;
            } else {
                updateObstacleShape(segIdx, obsIdx, finalCords);

                // mark dirty
                markObstacleDirty(obstruction);

                const finalCordsSnapshot = obstruction.cords.map(p => p.clone());

                if (translateState.moved) {
                    pushUndoAction({
                        label: "Move obstacle",
                        undo: () => {
                            const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                            if (!o) return;
                            updateObstacleShape(segIdx, obsIdx, originalCords);
                            o.isFlushed = originalIsFlushed;
                            o.linkedSegments = Array.isArray(originalLinkedSegments) ? originalLinkedSegments.slice() : [];
                            updateLinkedSegmentsAfterMove(o);
                        },
                        redo: () => {
                            const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                            if (!o) return;
                            updateObstacleShape(segIdx, obsIdx, finalCordsSnapshot);
                            markObstacleDirty(o);
                        },
                    });
                }
            }

            // sync handles to current geometry
            const pts = obstruction.cords;
            const basePointsUpdated = pts.slice(0, pts.length - 1);
            const finalCenter = new THREE.Vector3();
            basePointsUpdated.forEach(p => finalCenter.add(p));
            finalCenter.multiplyScalar(1 / basePointsUpdated.length);

            for (let i = 0; i < obstacleHandles.length; i++) {
                obstacleHandles[i].position.copy(basePointsUpdated[Math.min(i, basePointsUpdated.length - 1)]);
            }

            const handleOffsetLocal = 0.5;
            for (let i = 0; i < rotationHandles.length; i++) {
                const corner = basePointsUpdated[i].clone();
                const dirFromCenter = corner.clone().sub(finalCenter);
                let handlePos;
                if (dirFromCenter.lengthSq() > 0.0001) {
                    const dirNorm = dirFromCenter.clone().normalize();
                    handlePos = corner.clone().add(dirNorm.multiplyScalar(handleOffsetLocal));
                } else {
                    handlePos = corner.clone().add(new THREE.Vector3(0, handleOffsetLocal, 0));
                }
                rotationHandles[i].position.copy(handlePos);
                rotationHandles[i].userData.baseDir = handlePos.clone().sub(finalCenter);
            }

            for (let i = 0; i < edgeHandles.length; i++) {
                const p1 = basePointsUpdated[i % basePointsUpdated.length];
                const p2 = basePointsUpdated[(i + 1) % basePointsUpdated.length];
                const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

                edgeHandles[i].position.copy(mid);
                edgeHandles[i].userData.centerOffset = mid.clone().sub(finalCenter);
            }
        }

        currentDragMode = null;
        translateState = null;

        if (sceneManager.controls) sceneManager.controls.enabled = true;
        setDomCursor(sceneManager.renderer.domElement, 'auto');

        if (obstacleDragControls) obstacleDragControls.activate();
        if (rotationDragControls) rotationDragControls.activate();
    });

    attachHoverCursorListener(segmentIndex, obstacleIndex);
    attachPointerDownExitListener();
}

export function disableObstacleVertexDrag() {
    const domElement = sceneManager.renderer.domElement;

    currentDragMode = null;

    detachPointerDownExitListener();
    detachHoverCursorListener();

    if (obstacleDragControls) {
        obstacleDragControls.deactivate();
        obstacleDragControls.dispose();
        obstacleDragControls = null;
    }

    if (rotationDragControls) {
        rotationDragControls.deactivate();
        rotationDragControls.dispose();
        rotationDragControls = null;
    }

    if (edgeDragControls) {
        edgeDragControls.deactivate();
        edgeDragControls.dispose();
        edgeDragControls = null;
    }

    obstacleHandles.forEach(h => {
        h.removeFromParent();
        if (h.geometry) h.geometry.dispose();
        if (h.material) h.material.dispose();
    });
    obstacleHandles = [];

    rotationHandles.forEach(h => {
        h.removeFromParent();
        if (h.geometry) h.geometry.dispose();
        if (h.material) h.material.dispose();
    });
    rotationHandles = [];

    edgeHandles.forEach(h => {
        h.removeFromParent();
        if (h.geometry) h.geometry.dispose();
        if (h.material) h.material.dispose();
    });
    edgeHandles = [];

    rotationState = null;
    translateState = null;
    vertexDragState = null;

    if (active) {
        const { segmentIndex, obstacleIndex } = active;
        const obstruction = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
        if (obstruction) {
            setObstacleRaycastEnabled(obstruction, true);
        }
    }

    active = null;

    setDomCursor(domElement, 'auto');
}
